import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../lib/usePageTitle';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/Button';
import { api, ApiError } from '../../lib/api';

/** Gestione dati calcio: import da football-data.org on-demand. */
export function AdminDataPage() {
  usePageTitle('Dati calcio — Amministrazione');
  const { announce } = useAnnouncer();
  const [busy, setBusy] = useState<string | null>(null);
  const [output, setOutput] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['admin', 'import-status'],
    queryFn: () => api<{ configured: boolean }>('/admin/import/status'),
  });
  const seasons = useQuery({
    queryKey: ['seasons'],
    queryFn: () => api<{ seasons: Array<{ id: string; label: string }> }>('/seasons').then((r) => r.seasons),
  });

  const run = async (kind: 'season' | 'results' | 'tiers', seasonId: string, label: string) => {
    setBusy(`${kind}-${seasonId}`);
    setOutput(null);
    try {
      const res = await api<{ message: string; stats: Record<string, number> }>(
        `/admin/import/${kind}/${seasonId}`,
        { method: 'POST', body: {} },
      );
      const details = Object.entries(res.stats)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      setOutput(`${res.message} (${details})`);
      announce(res.message);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : `Import ${label} non riuscito.`;
      setOutput(`Errore: ${msg}`);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminLayout title="Dati calcio">
      <p className="mt-4 max-w-prose text-ink-700 dark:text-ink-200">
        I risultati e le fasce si aggiornano da soli ogni 30 minuti. Qui puoi forzare un
        aggiornamento immediato o reimportare il calendario completo.
      </p>

      {status.data && !status.data.configured && (
        <p role="alert" className="mt-4 rounded-lg border-2 border-warning bg-amber-50 p-3 font-medium text-warning dark:bg-ink-800">
          FOOTBALL_DATA_API_KEY non configurata sul server: gli import non sono disponibili.
        </p>
      )}

      {output && (
        <p role="status" className="mt-4 rounded-lg border border-ink-200 p-3 dark:border-ink-800">
          {output}
        </p>
      )}

      {seasons.data?.map((season) => (
        <section
          key={season.id}
          aria-label={`Import per ${season.label}`}
          className="mt-6 rounded-2xl border border-ink-200 p-5 dark:border-ink-800"
        >
          <h2 className="text-xl font-bold">{season.label}</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              variant="secondary"
              onClick={() => run('results', season.id, 'risultati')}
              loading={busy === `results-${season.id}`}
              loadingText="Import risultati…"
            >
              Aggiorna risultati
            </Button>
            <Button
              variant="secondary"
              onClick={() => run('tiers', season.id, 'fasce')}
              loading={busy === `tiers-${season.id}`}
              loadingText="Aggiornamento fasce…"
            >
              Aggiorna fasce
            </Button>
            <Button
              variant="secondary"
              onClick={() => run('season', season.id, 'calendario')}
              loading={busy === `season-${season.id}`}
              loadingText="Import calendario…"
            >
              Reimporta calendario completo
            </Button>
          </div>
        </section>
      ))}
    </AdminLayout>
  );
}
